import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import qs from 'query-string'
import { useLazyQuery } from '@apollo/react-hooks'
import { path } from 'ramda'
import { useDispatch } from 'react-redux'

import { modalContent } from '../../../shared/subscription-constants'
import VOUCHER_WITH_SKU_QUERY from '../../../../../graphql/queries/voucher-with-sku.gql'
import { getGQLErrorMsg, getGQLErrorCode, getGQLErrorDetails } from '../../../../lib/apollo'
import { MW_ERRORS } from '../../../../constants'
import {
  setVoucherValidateError,
  setVoucherValidateType
} from '../../../../actions'
import { usePlanDetails } from '../../../shared/hooks/use-plan-details'
import { useWelcomeBack } from '../../../../components/subscriptions/neon/welcome-back/hooks/use-welcome-back'
import { STORAGE_KEY_VOUCHER_PREFILLED } from '../../../../storage-keys'
import VoucherForm from '../../../shared/components/voucher-form'

const SubscriptionVoucherModal = () => {
  // Read and consume any prefilled voucher code from session storage
  const prefilledVoucher = sessionStorage.getItem(STORAGE_KEY_VOUCHER_PREFILLED) || ''
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [voucher, setVoucher] = useState(prefilledVoucher)
  const { getPlanTitleBySku, getPlanSkuByType } = usePlanDetails()
  const { isDefaultProfileIncomplete } = useWelcomeBack()

  useEffect(() => {
    dispatch(setVoucherValidateError(''))
    dispatch(setVoucherValidateType(''))
  }, [])

  const [validateVoucher] = useLazyQuery(VOUCHER_WITH_SKU_QUERY, {
    onCompleted: result => {
      setSubmitting(false)
      const voucherType = path(['voucherWithSku', 'type'], result)
      if (voucherType !== 'SVOD') {
        setError('Only subscription vouchers are accepted')
      } else {
        history.push({
          ...location,
          search: qs.stringify({
            ...qs.parse(location.search),
            content: getNextContent(),
            voucher
          })
        })
      }
    },
    onError: errorResult => {
      setSubmitting(false)

      const errorMsg = getGQLErrorMsg(errorResult)
      const errorCode = getGQLErrorCode(errorResult)
      const errorDetails = getGQLErrorDetails(errorResult)

      setError(errorMsg)

      if (errorCode === MW_ERRORS.VOUCHER_NOT_APPLICABLE) {
        dispatch(setVoucherValidateError(errorMsg))
        dispatch(setVoucherValidateType(getPlanTitleBySku(errorDetails.voucherSku)))

        history.push({
          ...location,
          search: qs.stringify({
            ...qs.parse(location.search),
            content: modalContent.voucherValidateErrorAfterLogin
          })
        })
      }
    }
  })

  const addVoucherHandler = () => {
    // Clear prefilled voucher from session storage now that the form is submitted
    sessionStorage.removeItem(STORAGE_KEY_VOUCHER_PREFILLED)
    setSubmitting(true)
    const { plan } = qs.parse(location.search)
    validateVoucher({
      variables: {
        voucherCode: voucher,
        signupSku: getPlanSkuByType(plan)
      }
    })
  }

  /**
   * Checking user has credit card status to get next content params
   */
  const getNextContent = () => {
    return isDefaultProfileIncomplete ? modalContent.accountDetails : modalContent.payment
  }

  /**
   * Handle skip button
   */
  const skipVoucherHandler = () => {
    history.push({
      ...location,
      search: qs.stringify({
        ...qs.parse(location.search),
        content: getNextContent()
      })
    })
  }

  return (
    <VoucherForm
      voucherValue={voucher}
      onVoucherChange={setVoucher}
      onRedeem={addVoucherHandler}
      onSkip={skipVoucherHandler}
      error={error}
      isSubmitting={submitting}
    />
  )
}

export default SubscriptionVoucherModal
